import { useState, useCallback } from 'react'

import CloseIcon from '@mui/icons-material/Close';
import GroupIcon from "@mui/icons-material/Group";

import { Button } from '../../../../common/APICommon';
import { WorkflowStatus } from '../../../../utils/enums';
import { ConfirmationModal } from '../../../../common/Modal';
import { MRFInformation } from './tabs/MRFInformatin';
import { CandidateForms } from './tabs/CandidateDetails';

export const HRRecruitmentForm = ({
  visible, onClose, onSave, onAccept, modalName, data, form, setForm, showGeneralMessage
}) => {
  const [activeTab, setActiveTab] = useState("mrf");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState({});

  const memoizedSetForm = useCallback(setForm, [setForm]);

  if (!visible) return null;

  const handleSubmit = async () => {
    if (!form.candidates || form.candidates.length === 0) {
      showGeneralMessage("Please add at least one candidate before submitting.", false);
      return;
    }

    // Updated validation to check for firstName and lastName instead of fullName
    const incompleteCandidates = form.candidates.filter(candidate =>
      !candidate.firstName || !candidate.lastName || !candidate.email || !candidate.contactNumber || !candidate.skills
    );

    if (incompleteCandidates.length > 0) {
      showGeneralMessage(`Please complete all required fields for ${incompleteCandidates.length} candidate(s).`, false);
      return;
    }

    const submissionData = {
      ...form,
      mrfId: data?.id,
      status: data?.status,
      submittedCandidatesCount: form.candidates.length,
    };

    await onSave(submissionData);
    closeModal();
  };

  const handleAcceptModal = () => {
    setConfirmConfig({
      title: "Process Request",
      message: "Are you sure you want to process this request?",
      confirmText: "Yes, Process",
      type: "info",
      onConfirm: handleAccept
    });
    setShowConfirmModal(true);
  }

  const handleAccept = async () => {
    setShowConfirmModal(false);
    await onAccept();
    closeModal();
  }

  const resetLocalVariables = () => {
    setActiveTab("mrf");
  };

  const closeModal = () => {
    onClose();
    resetLocalVariables();
  };

  return (
    <>
      <div
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 z-[10000] flex items-center justify-center overflow-auto bg-black/50 p-3 text-gray-700"
      >
        <div className="relative m-2 flex h-[90vh] w-full flex-col rounded-lg bg-gray-50 shadow-xl md:m-0 md:w-[90%] lg:w-[80%]">

          <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-lg border-b border-gray-200 bg-white px-6 py-2 shadow-sm">
            <h2 className="flex items-center space-x-2 text-xl font-semibold text-gray-800">
              <GroupIcon fontSize="large" className="text-green-400" />
              <span className="text-xl text-gray-600">{modalName}</span>
            </h2>

            <button
              onClick={closeModal}
              className="border-none bg-transparent p-1 text-gray-400 transition-colors hover:text-gray-700"
              aria-label="Close modal"
            >
              <CloseIcon fontSize="medium" />
            </button>
          </div>

          <div className="border-b border-gray-200 bg-white pt-3">
            <div className="flex space-x-1 px-6">
              <button
                onClick={() => setActiveTab("mrf")}
                className={`px-4 py-3 text-sm font-medium rounded-t-lg transition-colors border-b-2 ${activeTab === "mrf"
                    ? "border-blue-500 text-blue-600 bg-blue-50"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}
              >
                MRF Information
              </button>
              <button
                disabled={data?.status === WorkflowStatus.CANCELLED || data?.status === WorkflowStatus.PENDING}
                onClick={() => setActiveTab("candidates")}
                className={`px-4 py-3 text-sm font-medium rounded-t-lg transition-colors border-b-2 ${activeTab === "candidates"
                    ? "border-blue-500 text-blue-600 bg-blue-50"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}
              >
                Candidate Details ({form.candidates?.length || 0}/{data?.numberOfVacancies || 0})
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === "mrf" && (
              <MRFInformation data={data} />
            )}

            {(activeTab === "candidates") && (
              <CandidateForms
                data={data}
                form={form}
                setForm={memoizedSetForm}
                showGeneralMessage={showGeneralMessage}
              />
            )}
          </div>

          <div className="sticky bottom-0 z-10 flex justify-end space-x-2 rounded-b-lg border-t border-gray-200 bg-white p-6 shadow-sm">
            <button onClick={closeModal} className="cursor-pointer rounded-md border-none bg-gray-400 px-5 py-3 text-white transition-colors hover:bg-gray-500">
              Cancel
            </button>

            {data?.status === WorkflowStatus.PROCESSING && (
              <Button
                name="Submit Recruitment"
                onClick={handleSubmit}
                title="Submit the candidates"
                className="bg-blue-500 hover:bg-blue-600"
              />
            )}

            {data?.status === WorkflowStatus.PENDING && (
              <Button
                name="Process this Request"
                onClick={handleAcceptModal}
                title="Process this MRF request"
                className="bg-green-500 hover:bg-green-600"
              />
            )}
          </div>
        </div>
      </div>

      <ConfirmationModal
        visible={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={confirmConfig.onConfirm}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText={confirmConfig.confirmText}
        type={confirmConfig.type}
      />
    </>
  );
};